import React from 'react';

import adminApi from '../../Api/adminApi';
import useAdminStyles from './adminStyles';
import useAdminQuery from './useAdminQuery';
import StateCard from './StateCard';
import StatTile from './StatTile';
import LoginHeatmap from './LoginHeatmap';
import RevenueTable from './RevenueTable';
import EmailStatsTable from './EmailStatsTable';
import { fmtMoney } from './adminFormat';

function countFor(byPlanStatus, plan, status) {
    return (byPlanStatus || [])
        .filter((row) => (plan ? row.plan === plan : true) && (status ? row.status === status : true))
        .reduce((sum, row) => sum + (Number(row.count) || 0), 0);
}

const Section = ({ kicker, children }) => {
    const classes = useAdminStyles();
    return (
        <section className={classes.section}>
            <span className={classes.kicker}>{kicker}</span>
            {children}
        </section>
    );
};

/**
 * Key numbers for the whole platform, grouped: users, activity, lessons,
 * subscriptions, revenue, then the login heatmap and email campaign stats.
 */
const OverviewTab = () => {
    const classes = useAdminStyles();
    const { data, isLoading, isError, isForbidden } = useAdminQuery(() => adminApi.getOverview(), []);

    if (!data) {
        return <StateCard isLoading={isLoading} isError={isError} isForbidden={isForbidden} />;
    }

    const { users, activity, lessons, subscriptions, revenue } = data;
    const bps = subscriptions.byPlanStatus;

    return (
        <>
            <Section kicker="Users">
                <div className={classes.tileGrid}>
                    <StatTile label="Registered users" value={users.total} hint="Excludes guest accounts" />
                    <StatTile label="Guests" value={users.guests} hint="Temporary lesson guests" />
                    <StatTile label="New in 7 days" value={users.new7d} />
                    <StatTile label="New in 30 days" value={users.new30d} />
                </div>
            </Section>

            <Section kicker="Activity">
                <div className={classes.tileGrid}>
                    <StatTile label="Daily active" value={activity.dau} hint="Registered users who signed in, last 24 h" />
                    <StatTile label="Weekly active" value={activity.wau} hint="Registered users who signed in, last 7 days" />
                    <StatTile label="Monthly active" value={activity.mau} hint="Registered users who signed in, last 30 days" />
                    <StatTile
                        label="Live lessons now"
                        value={lessons.liveNow}
                        accent={lessons.liveNow > 0}
                        hint="Classes with an active heartbeat"
                    />
                </div>
            </Section>

            <Section kicker="Lessons">
                <div className={classes.tileGrid}>
                    <StatTile label="Today" value={lessons.today} hint="Teacher sessions, last 24 h" />
                    <StatTile label="Last 7 days" value={lessons.last7d} />
                    <StatTile label="Last 30 days" value={lessons.last30d} />
                </div>
            </Section>

            <Section kicker="Subscriptions">
                <div className={classes.tileGrid}>
                    <StatTile label="Premium" value={countFor(bps, 'PREMIUM', 'active')} hint="Active, paid" />
                    <StatTile label="Basic" value={countFor(bps, 'BASIC', 'active')} hint="Active, paid" />
                    <StatTile label="Special" value={countFor(bps, 'SPECIAL', 'active')} hint="Granted manually" />
                    <StatTile label="Free" value={countFor(bps, 'FREE', 'free')} />
                    <StatTile label="Trials running" value={countFor(bps, 'TRIAL', 'trial')} />
                    <StatTile label="Trials expired" value={countFor(bps, 'TRIAL', 'expired')} hint="Not yet converted" />
                    <StatTile
                        label="Cancellations pending"
                        value={countFor(bps, null, 'canceled')}
                        accent={countFor(bps, null, 'canceled') > 0}
                        hint="Canceled, period still running"
                    />
                    <StatTile label="Expired paid plans" value={countFor(bps, 'BASIC', 'expired') + countFor(bps, 'PREMIUM', 'expired')} hint="Past the grace period" />
                    <StatTile label="Ending in 7 days" value={subscriptions.expiring7d} hint="Trials and canceled plans" />
                    <StatTile label="Ending in 30 days" value={subscriptions.expiring30d} hint="Trials and canceled plans" />
                    <StatTile label="Renewing in 7 days" value={subscriptions.renewing7d} hint="Paid plans not canceled" />
                    <StatTile label="Renewing in 30 days" value={subscriptions.renewing30d} hint="Paid plans not canceled" />
                </div>
            </Section>

            <Section kicker="Revenue">
                <div className={classes.tileGrid}>
                    {revenue.byCurrency.length === 0 ? (
                        <StatTile label="Paid invoices" value="—" hint="No live invoice.paid events stored yet" />
                    ) : null}
                    {revenue.byCurrency.map((row) => (
                        <React.Fragment key={row.currency}>
                            <StatTile
                                label={`Last 30 days (${(row.currency || '').toUpperCase()})`}
                                value={fmtMoney(row.last30d, row.currency)}
                                hint={`${row.invoices30d} invoice(s)`}
                            />
                            <StatTile
                                label={`Last 12 months (${(row.currency || '').toUpperCase()})`}
                                value={fmtMoney(row.last12m, row.currency)}
                            />
                        </React.Fragment>
                    ))}
                    <StatTile
                        label="Failed payments"
                        value={revenue.failed30d}
                        accent={revenue.failed30d > 0}
                        hint="Last 30 days"
                    />
                </div>
                <div className={classes.card} style={{ marginTop: '14px' }}>
                    <h3 className={classes.cardTitle}>Paid invoices by month</h3>
                    <RevenueTable monthly={revenue.monthly} />
                </div>
            </Section>

            <Section kicker="Logins, last 100 days">
                <div className={classes.card}>
                    <LoginHeatmap counts={data.loginCounts} />
                </div>
            </Section>

            <Section kicker="Email campaigns">
                <div className={classes.card}>
                    <EmailStatsTable emailsSent={data.emailsSent} emailsClicked={data.emailsClicked} />
                </div>
            </Section>
        </>
    );
};

export default OverviewTab;
